import React from "react";
import { connect } from "react-redux";
import { Badge, Form, Icon, Layout, Switch, Tabs } from "antd";
import { withNamespaces } from "react-i18next";
import get from "lodash/get";

import RandomizationConfigForm from "./RandomizationConfigForm";
import DrugManagementConfigForm from "./DrugManagementConfigForm";
import DocumentsConfigForm from "./DocumentsConfigForm";
import ConsentConfigForm from "./ConsentConfigForm";
import DelLogConfigForm from "./DelLogConfigForm";
import InboundApiConfigForm from "./InboundApiConfigForm";
import AuthConfigForm from "./AuthConfigForm";
import classes from "./WizardConfig.css";

import isAuthenticated from "../../utils/isAuthenticated";
import { logout, setAppSettings } from "../../reducers/user/actions";
import {
  getProjectConfig,
  getDocumentsConfig,
  getDrugManagementConfig,
  getRandomizationConfig,
  lockProjectConfig,
  unlockProjectConfig,
  getConsentConfig,
  getDelLogConfig,
  getInboundApiConfig,
  getAuthConfig,
} from "../../api/requests";
import {
  getProjectConfigurationStatus,
  getUpdateDocumentConfigs,
  getUpdateDrugDosageConfigs,
  getUpdateConsentConfigs,
  getUpdateRandomizationsConfigs,
  getUpdateDelegationLogConfigsPermission,
  getUpdateInboundApiConfigs,
  getAuthConfigUpdatePermission,
} from "../../reducers/user/selectors";

const { Content } = Layout;
const { TabPane } = Tabs;

class WizardConfig extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      randomizationData: null,
      drugManagementData: null,
      projectConfigData: null,
      consentConfigData: null,
      documentsConfigData: null,
      authConfigData: null,
      isLoadingData: true,
      projectConfigLoading: false,
    };
  }

  componentDidMount() {
    this.getConfigData();
    this.getProjectConfigData();
  }

  getConfigData = async () => {
    this.setState({
      isLoadingData: true,
    });

    try {
      if (this.props.updateDrugDosageConfigs) {
        this.getDrugManagementConfig();
      }

      if (this.props.updateRandomizationsConfigs) {
        this.getRandomizationConfig();
      }

      if (this.props.updateConsentConfigs) {
        this.getConsentConfig();
      }

      if (this.props.updateDocumentConfigs) {
        this.getDocumentsConfig();
      }

      if (this.props.updateDelegationLogConfigsPermission) {
        this.getDelLogConfig();
      }

      if (this.props.updateInboundApisConfigs) {
        this.getInboundApiConfig();
      }

      if (this.props.getAuthConfigUpdatePermission) {
        this.getSamlConfig();
      }
    } catch (e) {
      console.log(e);
    }

    this.setState({
      isLoadingData: false,
    });
  };

  getDrugManagementConfig = async () => {
    try {
      const { data: drugManagementData } = await getDrugManagementConfig();
      this.setState({ drugManagementData });
    } catch (e) {
      console.log(e);
    }
  };

  getRandomizationConfig = async () => {
    try {
      const { data: randomizationData } = await getRandomizationConfig();
      this.setState({ randomizationData });
    } catch (e) {
      console.log(e);
    }
  };

  getDocumentsConfig = async () => {
    try {
      const { data: documentsConfigData } = await getDocumentsConfig();
      this.setState({ documentsConfigData });
    } catch (e) {
      console.log(e);
    }
  };

  getConsentConfig = async () => {
    try {
      const { data: consentConfigData } = await getConsentConfig();
      this.setState({ consentConfigData });
    } catch (e) {
      console.log(e);
    }
  };

  getDelLogConfig = async () => {
    try {
      const { data: delegationLogConfigData } = await getDelLogConfig();
      this.setState({ delegationLogConfigData });
    } catch (e) {
      console.log(e);
    }
  };

  getInboundApiConfig = async () => {
    try {
      const { data: inboundApiConfigData } = await getInboundApiConfig();
      this.setState({ inboundApiConfigData });
    } catch (e) {
      console.log(e);
    }
  };

  getSamlConfig = async () => {
    try {
      const { data: authConfigData } = await getAuthConfig();
      this.setState({ authConfigData });
    } catch (e) {
      console.log(e);
    }
  };

  getProjectConfigData = async () => {
    this.setState({
      projectConfigLoading: true,
    });

    try {
      const [projectConfig] = await Promise.all([getProjectConfig()]);

      const { data: projectConfigData } = projectConfig;

      this.setState({
        projectConfigData,
      });
    } catch (e) {
      console.log(e);
    }

    this.setState({
      projectConfigLoading: false,
    });
  };

  onSubmit = () => {
    this.getConfigData();
    this.props.setAppSettings();
  };

  onProjectLockStatus = (val) => async () => {
    this.setState({
      projectConfigLoading: true,
    });

    if (!val) {
      await lockProjectConfig();
    } else {
      await unlockProjectConfig();
    }

    await this.getProjectConfigData();

    this.setState({
      projectConfigLoading: false,
    });
  };

  render() {
    const {
      randomizationData,
      drugManagementData,
      projectConfigData,
      consentConfigData,
      delegationLogConfigData,
      inboundApiConfigData,
      documentsConfigData,
      authConfigData,
      isLoadingData,
      projectConfigLoading,
    } = this.state;

    const { isProjectConfigurationActive } = this.props;

    return (
      <div>
        <Layout className={classes.parentContainer}>
          <Content className={classes.content}>
            <div>
              <h3 className="headline-default">
                Project Lock status{" "}
                <Badge
                  count={
                    isProjectConfigurationActive
                      ? this.props.i18n.t("NamespaceWizardConfig:Configured")
                      : this.props.i18n.t("NamespaceWizardConfig:In progress")
                  }
                  style={{
                    backgroundColor: isProjectConfigurationActive
                      ? "#49c850"
                      : "#999",
                  }}
                />
              </h3>

              <div>
                <Switch
                  checked={get(projectConfigData, "is_locked")}
                  onChange={this.onProjectLockStatus(
                    get(projectConfigData, "is_locked")
                  )}
                  loading={projectConfigLoading}
                  disabled={!isProjectConfigurationActive}
                />
                &nbsp;
                {get(projectConfigData, "is_locked")
                  ? this.props.i18n.t("Locked")
                  : this.props.i18n.t("Unlocked")}
              </div>
            </div>

            <br />

            <Tabs
              defaultActiveKey="1"
              animated={{ inkBar: true, tabPane: false }}
            >
              {this.props.updateRandomizationsConfigs && (
                <TabPane
                  tab={
                    <div>
                      {this.props.i18n.t(
                        "NamespaceWizardConfig:Randomization Config"
                      )}{" "}
                      <div>
                        {randomizationData && (
                          <Badge
                            count={
                              get(randomizationData, "is_configured")
                                ? this.props.i18n.t(
                                    "NamespaceWizardConfig:Configured"
                                  )
                                : this.props.i18n.t(
                                    "NamespaceWizardConfig:Needs to be configured"
                                  )
                            }
                            style={{
                              backgroundColor: get(
                                randomizationData,
                                "is_configured"
                              )
                                ? "#49c850"
                                : "#999",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  }
                  key="1"
                >
                  {randomizationData && (
                    <RandomizationConfigForm
                      onSubmit={this.onSubmit}
                      isLoadingData={isLoadingData}
                      randomizationData={randomizationData}
                      isDisabled={get(projectConfigData, "is_locked")}
                    />
                  )}
                </TabPane>
              )}
              {this.props.updateDrugDosageConfigs && (
                <TabPane
                  tab={
                    <div>
                      {this.props.i18n.t(
                        "NamespaceWizardConfig:Drug Management Config"
                      )}{" "}
                      <div>
                        {drugManagementData && (
                          <Badge
                            count={
                              get(drugManagementData, "is_configured")
                                ? this.props.i18n.t(
                                    "NamespaceWizardConfig:Configured"
                                  )
                                : this.props.i18n.t(
                                    "NamespaceWizardConfig:Needs to be configured"
                                  )
                            }
                            style={{
                              backgroundColor: get(
                                drugManagementData,
                                "is_configured"
                              )
                                ? "#49c850"
                                : "#999",
                            }}
                          />
                        )}{" "}
                        {drugManagementData && (
                          <Badge
                            count={
                              get(drugManagementData, "drug_management_active")
                                ? this.props.i18n.t("Active")
                                : this.props.i18n.t("Inactive")
                            }
                            style={{
                              backgroundColor: get(
                                drugManagementData,
                                "drug_management_active"
                              )
                                ? "#49c850"
                                : "#999",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  }
                  key="2"
                >
                  {randomizationData && drugManagementData && (
                    <DrugManagementConfigForm
                      onSubmit={this.onSubmit}
                      isLoadingData={isLoadingData}
                      randomizationData={randomizationData}
                      drugManagementData={drugManagementData}
                      isDisabled={get(projectConfigData, "is_locked")}
                    />
                  )}
                </TabPane>
              )}
              {this.props.updateDocumentConfigs && (
                <TabPane
                  tab={
                    <div>
                      {this.props.i18n.t(
                        "NamespaceWizardConfig:Documents Config"
                      )}{" "}
                      <div>
                        {documentsConfigData && (
                          <Badge
                            count={
                              get(documentsConfigData, "is_configured")
                                ? this.props.i18n.t(
                                    "NamespaceWizardConfig:Configured"
                                  )
                                : this.props.i18n.t(
                                    "NamespaceWizardConfig:Needs to be configured"
                                  )
                            }
                            style={{
                              backgroundColor: get(
                                documentsConfigData,
                                "is_configured"
                              )
                                ? "#49c850"
                                : "#999",
                            }}
                          />
                        )}{" "}
                      </div>
                    </div>
                  }
                  key="3"
                >
                  {randomizationData &&
                    drugManagementData &&
                    documentsConfigData && (
                      <DocumentsConfigForm
                        onSubmit={this.onSubmit}
                        isLoadingData={isLoadingData}
                        randomizationData={randomizationData}
                        drugManagementData={drugManagementData}
                        documentsConfigData={documentsConfigData}
                        isDisabled={get(projectConfigData, "is_locked")}
                      />
                    )}
                </TabPane>
              )}
              {this.props.updateConsentConfigs && (
                <TabPane
                  tab={
                    <div>
                      {this.props.i18n.t(
                        "NamespaceWizardConfig:Consent Config"
                      )}{" "}
                      <div>
                        {consentConfigData && (
                          <Badge
                            count={
                              get(consentConfigData, "is_configured")
                                ? this.props.i18n.t(
                                    "NamespaceWizardConfig:Configured"
                                  )
                                : this.props.i18n.t(
                                    "NamespaceWizardConfig:Needs to be configured"
                                  )
                            }
                            style={{
                              backgroundColor: get(
                                consentConfigData,
                                "is_configured"
                              )
                                ? "#49c850"
                                : "#999",
                            }}
                          />
                        )}{" "}
                      </div>
                    </div>
                  }
                  key="4"
                >
                  {consentConfigData && (
                    <ConsentConfigForm
                      onSubmit={this.onSubmit}
                      isLoadingData={isLoadingData}
                      consentConfigData={consentConfigData}
                      randomizationData={randomizationData}
                      isDisabled={get(projectConfigData, "is_locked")}
                    />
                  )}
                </TabPane>
              )}
              {this.props.updateDelegationLogConfigsPermission && (
                <TabPane
                  tab={
                    <div>
                      {this.props.i18n.t(
                        "NamespaceWizardConfig:Delegation Log Config"
                      )}{" "}
                      <div>
                        {delegationLogConfigData && (
                          <Badge
                            count={
                              get(delegationLogConfigData, "is_configured")
                                ? this.props.i18n.t(
                                    "NamespaceWizardConfig:Configured"
                                  )
                                : this.props.i18n.t(
                                    "NamespaceWizardConfig:Needs to be configured"
                                  )
                            }
                            style={{
                              backgroundColor: get(
                                delegationLogConfigData,
                                "is_configured"
                              )
                                ? "#49c850"
                                : "#999",
                            }}
                          />
                        )}{" "}
                      </div>
                    </div>
                  }
                  key="5"
                >
                  {delegationLogConfigData && (
                    <DelLogConfigForm
                      onSubmit={this.onSubmit}
                      isLoadingData={isLoadingData}
                      delegationLogConfigData={delegationLogConfigData}
                      randomizationData={randomizationData}
                      isDisabled={get(projectConfigData, "is_locked")}
                    />
                  )}
                </TabPane>
              )}
              {this.props.updateInboundApisConfigs && (
                <TabPane
                  tab={
                    <div>
                      {this.props.i18n.t(
                        "NamespaceWizardConfig:Inbound Apis Config"
                      )}{" "}
                      <br />
                      <i>
                        {this.props.i18n.t(
                          "NamespaceWizardConfig:Third party apis that are accessing the DCTMS server via api direct calls"
                        )}
                      </i>
                      <div>
                        {inboundApiConfigData && (
                          <Badge
                            count={
                              get(inboundApiConfigData, "is_configured")
                                ? this.props.i18n.t(
                                    "NamespaceWizardConfig:Configured"
                                  )
                                : this.props.i18n.t(
                                    "NamespaceWizardConfig:Needs to be configured"
                                  )
                            }
                            style={{
                              backgroundColor: get(
                                inboundApiConfigData,
                                "is_configured"
                              )
                                ? "#49c850"
                                : "#999",
                            }}
                          />
                        )}{" "}
                      </div>
                    </div>
                  }
                  key="6"
                >
                  {inboundApiConfigData && (
                    <InboundApiConfigForm
                      onSubmit={this.onSubmit}
                      isLoadingData={isLoadingData}
                      inboundApiConfigData={inboundApiConfigData}
                      randomizationData={randomizationData}
                      isDisabled={get(projectConfigData, "is_locked")}
                    />
                  )}
                </TabPane>
              )}

              {this.props.getAuthConfigUpdatePermission && (
                <TabPane
                  tab={
                    <div>
                      {this.props.i18n.t("NamespaceWizardConfig:Auth Config")}{" "}
                      <div>
                        {authConfigData && (
                          <Badge
                            count={
                              get(authConfigData, "is_configured")
                                ? this.props.i18n.t(
                                    "NamespaceWizardConfig:Configured"
                                  )
                                : this.props.i18n.t(
                                    "NamespaceWizardConfig:Needs to be configured"
                                  )
                            }
                            style={{
                              backgroundColor: get(
                                authConfigData,
                                "is_configured"
                              )
                                ? "#49c850"
                                : "#999",
                            }}
                          />
                        )}{" "}
                      </div>
                    </div>
                  }
                  key="7"
                >
                  {authConfigData && <AuthConfigForm
                    onSubmit={this.onSubmit}
                    isLoadingData={isLoadingData}
                    authConfigData={authConfigData}
                    isDisabled={get(projectConfigData, "is_locked")}
                  />}
                </TabPane>
              )}
            </Tabs>
          </Content>
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  isProjectConfigurationActive: getProjectConfigurationStatus(state),
  updateDocumentConfigs: getUpdateDocumentConfigs(state),
  updateRandomizationsConfigs: getUpdateRandomizationsConfigs(state),
  updateConsentConfigs: getUpdateConsentConfigs(state),
  updateDrugDosageConfigs: getUpdateDrugDosageConfigs(state),
  updateInboundApisConfigs: getUpdateInboundApiConfigs(state),
  updateDelegationLogConfigsPermission: getUpdateDelegationLogConfigsPermission(
    state
  ),
  getAuthConfigUpdatePermission: getAuthConfigUpdatePermission(state),
});
const mapDispatchToProps = { setAppSettings, logout };

export default isAuthenticated(
  Form.create({ name: "randomizationConfig" })(
    connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(WizardConfig))
  )
);



// WEBPACK FOOTER //
// ./src/containers/WizardConfig/WizardConfig.js